import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/homeData';
import { categoryMovies, categoryInfo } from '@/mocks/categoryData';

export default function CategoryPage() {
  const { slug = 'dianying' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const currentMovies = categoryMovies[activeSlug] || categoryMovies.dianying;
  const info = categoryInfo[activeSlug];

  useEffect(() => {
    setActiveSlug(slug);
  }, [slug]);

  if (!info) {
    return (
      <>
        <SEOHead
          title="分类未找到"
          description="红桃影视影视分类"
          keywords="红桃影视,影视分类"
          canonicalUrl={`https://hongtaomovies.com/category/${slug}`}
        />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <p className="text-foreground-500">分类未找到</p>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={info.name}
        description={info.description}
        keywords={`红桃影视${info.name},红桃影视${info.name}在线观看,${info.name}高清资源`}
        canonicalUrl={`https://hongtaomovies.com/category/${activeSlug}`}
      />
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: info.name, path: `/category/${activeSlug}` },
              ]}
            />
            <div className="mt-4">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                红桃影视{info.name}
              </h1>
              <p className="text-foreground-500 text-sm">{info.description}</p>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto py-3 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-5">
              {currentMovies.map((movie) => (
                <div key={movie.id} className="group cursor-pointer">
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
                    <img
                      src={movie.image}
                      alt={movie.title}
                      title={`${movie.title} - 红桃影视${info.name}高清在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                        {movie.rating}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="text-xs text-background-200/80 bg-black/40 px-2 py-0.5 rounded-full">
                        {movie.year}
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1">
                      {movie.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs text-background-200/90 bg-black/30 px-2 py-0.5 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <h3 className="font-medium text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {movie.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1 line-clamp-2 leading-relaxed">
                    {movie.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}